import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
// import * as Sentry from "@sentry/react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SessionErrorBoundary } from "./components/SessionErrorBoundary";
import { MainLayout } from "./layouts/MainLayout";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { NewLandingPage } from "./pages/NewLandingPage";
import { AuthPage } from "./pages/AuthPage";
import { UpdatePasswordPage } from "./pages/UpdatePasswordPage";
import { DashboardPage } from "./pages/DashboardPage";
import { EnhancedGeneratePage as GeneratePage } from "./pages/EnhancedGeneratePage";
import { DocumentViewPage } from "./features/documents/pages/DocumentViewPage";
import { PricingPage } from "./features/payments/pages/PricingPage";
import { CheckoutPage } from "./features/payments/pages/CheckoutPage";
import { TermsPage } from "./features/legal/pages/TermsPage";
import { PrivacyPage } from "./features/legal/pages/PrivacyPage";
import { CookiesPage } from "./features/legal/pages/CookiesPage";
import {
  UserTypeSelectionPage,
  LawyerOnboardingPage,
  ClientOnboardingPage,
  EmailVerificationPage,
} from "./features/onboarding/pages";
// Lawyer feature pages
import { LawyerDashboardPage } from "./features/lawyer/pages/LawyerDashboardPage";
import { LawyerDocumentsPage } from "./features/lawyer/pages/LawyerDocumentsPage";
import { CasesPage } from "./features/lawyer/pages/CasesPage";
import { CaseDetailsPage } from "./features/lawyer/pages/CaseDetailsPage";
import { ClientsPage } from "./features/lawyer/pages/ClientsPage";
import { ClientDetailPage } from "./features/lawyer/pages/ClientDetailPage";
import { ConsultationsPage } from "./features/lawyer/pages/ConsultationsPage";
import { LegalChatPage } from "./features/lawyer/pages/LegalChatPage";
import { BillingReportsPage } from "./features/lawyer/pages/BillingReportsPage";
import { CaseSummaryPage } from "./features/lawyer/pages/CaseSummaryPage";
import { EditProfilePage } from "./features/lawyer/pages/EditProfilePage";
// Document Review pages
import { DocumentReviewPage } from "./features/document-review/pages/DocumentReviewPage";
import { ReviewDetailsPage } from "./features/document-review/pages/ReviewDetailsPage";

// Public pages
import { LawyerDirectoryPage } from "./features/public/pages/LawyerDirectoryPage";
import { PublicLawyerProfilePage } from "./features/public/pages/PublicLawyerProfilePage";
import { SettingsPage } from "./pages/SettingsPage";
// Admin feature pages
import { AdminLayout } from "./layouts/AdminLayout";
import { AdminRoute } from "./components/AdminRoute";
import { AdminDashboardPage } from "./features/admin/pages/AdminDashboardPage";
import { UsersManagementPage } from "./features/admin/pages/UsersManagementPage";
import { ExequaturVerificationPage } from "./features/admin/pages/ExequaturVerificationPage";
import { MaterialsManagementPage } from "./features/admin/pages/MaterialsManagementPage";
import { MetricsPage } from "./features/admin/pages/MetricsPage";
import { ActivityPage } from "./features/admin/pages/ActivityPage";
import { UserDetailPage } from "./features/admin/pages/UserDetailPage";
import { AdminSettingsPage } from "./features/admin/pages/AdminSettingsPage";
import { AdminReportsPage } from "./features/admin/pages/AdminReportsPage";
import { AdminCasesPage } from "./features/admin/pages/AdminCasesPage";
import { DocumentsModerationPage } from "./features/admin/pages/DocumentsModerationPage";
import { ROUTES } from "@/shared/core";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ReportErrorProvider } from "./contexts/ReportErrorContext";
import "./i18n/config";
import { Loading } from "@/shared/ui";
// Onboarding Route - Redirects to onboarding if needed
const OnboardingRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    user,
    profile,
    sessionStatus,
    needsOnboarding,
    getUserType,
    isAdmin,
    error,
    retrySession,
    signOut,
  } = useAuth();
  const location = useLocation();

  // Show loading for initialization states
  if (
    sessionStatus === "idle" ||
    sessionStatus === "initializing" ||
    sessionStatus === "hydrating" ||
    sessionStatus === "loading_profile" ||
    sessionStatus === "authenticating"
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  // Show error state
  if (sessionStatus === "error" && error) {
    return (
      <SessionErrorBoundary
        error={{
          type: "unknown",
          message: error,
          retryable: true,
          retryCount: 0,
        }}
        onRetry={retrySession}
        onSignOut={signOut}
      />
    );
  }

  // Redirect to auth if no user or token expired
  if (sessionStatus === "unauthenticated" || !user) {
    // Save the current path to redirect back after login
    if (
      location.pathname !== ROUTES.AUTH &&
      !location.pathname.startsWith("/onboarding")
    ) {
      sessionStorage.setItem(
        "redirectAfterAuth",
        location.pathname + location.search
      );
    }
    return <Navigate to={ROUTES.AUTH} replace />;
  }

  // Wait for profile to load before checking onboarding status
  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  // Admins don't need onboarding - redirect to admin dashboard
  if (isAdmin) {
    // If admin is trying to access regular dashboard, redirect to admin dashboard
    if (
      location.pathname === ROUTES.DASHBOARD ||
      location.pathname.startsWith("/dashboard")
    ) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    // If admin is trying to access onboarding, redirect to admin dashboard
    if (location.pathname.startsWith("/onboarding")) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    // Otherwise allow access (they might be accessing admin routes)
    return <>{children}</>;
  }

  // If user needs onboarding
  if (needsOnboarding()) {
    const userType = getUserType();

    // Save the current path to redirect back after onboarding
    if (
      !location.pathname.startsWith("/onboarding") &&
      !sessionStorage.getItem("redirectAfterOnboarding")
    ) {
      sessionStorage.setItem(
        "redirectAfterOnboarding",
        location.pathname + location.search
      );
    }

    // If no user type yet, go to selection page
    if (!userType) {
      return <Navigate to="/onboarding/select-type" replace />;
    }

    // If has user type but onboarding not completed, go to specific onboarding
    return <Navigate to={`/onboarding/${userType}`} replace />;
  }

  return <>{children}</>;
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, sessionStatus, error, retrySession, signOut } = useAuth();
  const location = useLocation();

  // Show loading for initialization states
  if (
    sessionStatus === "idle" ||
    sessionStatus === "initializing" ||
    sessionStatus === "hydrating" ||
    sessionStatus === "loading_profile" ||
    sessionStatus === "authenticating"
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500"></div>
      </div>
    );
  }

  // Show error state
  if (sessionStatus === "error" && error) {
    return (
      <SessionErrorBoundary
        error={{
          type: "unknown",
          message: error,
          retryable: true,
          retryCount: 0,
        }}
        onRetry={retrySession}
        onSignOut={signOut}
      />
    );
  }

  // Redirect to auth if no user or token expired
  if (sessionStatus === "unauthenticated" || !user) {
    // Save the current path to redirect back after login
    if (
      location.pathname !== ROUTES.AUTH &&
      !location.pathname.startsWith("/onboarding")
    ) {
      sessionStorage.setItem(
        "redirectAfterAuth",
        location.pathname + location.search
      );
    }
    return <Navigate to={ROUTES.AUTH} replace />;
  }

  return <>{children}</>;
};

// Public Only Route (redirects to dashboard if logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, sessionStatus, isAdmin, profile, getUserType } = useAuth();

  // Show loading for initialization states
  if (
    sessionStatus === "idle" ||
    sessionStatus === "initializing" ||
    sessionStatus === "hydrating" ||
    sessionStatus === "loading_profile"
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500"></div>
      </div>
    );
  }

  // If authenticated, redirect to appropriate dashboard
  if (sessionStatus === "authenticated" && user) {
    // Wait for profile to load to check admin status
    if (!profile) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500"></div>
        </div>
      );
    }

    // Admins go to admin dashboard, regular users go to user-specific dashboard
    if (isAdmin) {
      return <Navigate to="/admin/dashboard" replace />;
    }

    const userType = getUserType();
    if (userType) {
      return <Navigate to={`/dashboard/${userType}`} replace />;
    }

    // Fallback to generic dashboard if no userType (shouldn't happen after onboarding)
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <>{children}</>;
};

// Auth Callback Redirect - Detects Supabase auth callbacks (success or error) and redirects to verification page
const AuthErrorRedirect: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams(location.hash.substring(1));

  // Check for auth errors in both query and hash params
  const error = searchParams.get("error") || hashParams.get("error");

  // Check for successful auth callback (access_token in hash)
  const accessToken = hashParams.get("access_token");
  const type = hashParams.get("type"); // 'signup' or 'recovery'

  // Redirect to verification page for auth errors
  if (error === "access_denied") {
    return (
      <Navigate to={`/auth/verify${location.search}${location.hash}`} replace />
    );
  }

  // Redirect to verification page for successful signup verification
  if (accessToken && type === "signup") {
    return <Navigate to={`/auth/verify${location.hash}`} replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Landing - Sin Layout (with auth error detection) */}
      <Route
        path={ROUTES.HOME}
        element={
          <AuthErrorRedirect>
            <NewLandingPage />
          </AuthErrorRedirect>
        }
      />
      {/* Compatibility: legacy OAuth/links may return to /app */}
      <Route path="/app" element={<Navigate to={ROUTES.DASHBOARD} replace />} />

      {/* Auth - Con MainLayout simple */}
      <Route element={<MainLayout />}>
        <Route
          path={ROUTES.AUTH}
          element={
            <PublicRoute>
              <AuthPage />
            </PublicRoute>
          }
        />
        <Route path="/auth/update-password" element={<UpdatePasswordPage />} />
        <Route path="/auth/verify" element={<EmailVerificationPage />} />
        <Route path={ROUTES.TERMS} element={<TermsPage />} />
        <Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />
        <Route path={ROUTES.COOKIES} element={<CookiesPage />} />
      </Route>

      {/* Checkout - Protected */}
      <Route
        path={ROUTES.CHECKOUT}
        element={
          <ProtectedRoute>
            <CheckoutPage />
          </ProtectedRoute>
        }
      />

      {/* Public Pages - Sin Layout protegido */}
      <Route path="/lawyers" element={<LawyerDirectoryPage />} />
      <Route path="/lawyers/:lawyerId" element={<PublicLawyerProfilePage />} />

      {/* Onboarding - Sin Layout */}
      <Route
        path="/onboarding/select-type"
        element={<UserTypeSelectionPage />}
      />
      <Route
        path="/onboarding/lawyer"
        element={
          <ProtectedRoute>
            <LawyerOnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/client"
        element={
          <ProtectedRoute>
            <ClientOnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Dashboard - Con DashboardLayout (sidebar) - Requiere onboarding completado */}
      <Route element={<DashboardLayout />}>
        <Route
          path={ROUTES.DASHBOARD}
          element={
            <OnboardingRoute>
              <DashboardPage />
            </OnboardingRoute>
          }
        />
        <Route
          path={ROUTES.GENERATE}
          element={
            <OnboardingRoute>
              <GeneratePage />
            </OnboardingRoute>
          }
        />
        <Route
          path={`${ROUTES.DOCUMENT}/:id`}
          element={
            <OnboardingRoute>
              <DocumentViewPage />
            </OnboardingRoute>
          }
        />

        {/* Lawyer Routes */}
        <Route
          path="/dashboard/lawyer"
          element={
            <OnboardingRoute>
              <LawyerDashboardPage />
            </OnboardingRoute>
          }
        />
        <Route
          path="/dashboard/lawyer/documents"
          element={
            <OnboardingRoute>
              <LawyerDocumentsPage />
            </OnboardingRoute>
          }
        />
        <Route
          path="/dashboard/lawyer/cases"
          element={
            <OnboardingRoute>
              <CasesPage />
            </OnboardingRoute>
          }
        />
        <Route
          path="/dashboard/lawyer/cases/:caseId"
          element={
            <OnboardingRoute>
              <CaseDetailsPage />
            </OnboardingRoute>
          }
        />
        <Route
          path="/dashboard/lawyer/clients"
          element={
            <OnboardingRoute>
              <ClientsPage />
            </OnboardingRoute>
          }
        />
        <Route
          path="/dashboard/lawyer/clients/:clientId"
          element={
            <OnboardingRoute>
              <ClientDetailPage />
            </OnboardingRoute>
          }
        />
        <Route
          path="/dashboard/lawyer/consultations"
          element={
            <OnboardingRoute>
              <ConsultationsPage />
            </OnboardingRoute>
          }
        />

        <Route
          path="/dashboard/lawyer/profile/edit"
          element={
            <OnboardingRoute>
              <EditProfilePage />
            </OnboardingRoute>
          }
        />
        <Route
          path="/dashboard/lawyer/document-review"
          element={
            <OnboardingRoute>
              <DocumentReviewPage />
            </OnboardingRoute>
          }
        />
        <Route
          path="/dashboard/lawyer/document-review/:id"
          element={
            <OnboardingRoute>
              <ReviewDetailsPage />
            </OnboardingRoute>
          }
        />
        <Route
          path="/dashboard/lawyer/legal-chat"
          element={
            <OnboardingRoute>
              <LegalChatPage />
            </OnboardingRoute>
          }
        />
        <Route
          path="/dashboard/lawyer/billing-reports"
          element={
            <OnboardingRoute>
              <BillingReportsPage />
            </OnboardingRoute>
          }
        />
        <Route
          path="/dashboard/lawyer/case-summary"
          element={
            <OnboardingRoute>
              <CaseSummaryPage />
            </OnboardingRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <OnboardingRoute>
              <SettingsPage />
            </OnboardingRoute>
          }
        />
        {/* Pricing - Accessible from dashboard with topbar (no auth required) */}
        <Route path={ROUTES.PRICING} element={<PricingPage />} />
      </Route>

      {/* Admin Routes - Con AdminLayout */}
      <Route element={<AdminLayout />}>
        <Route
          path="/admin/dashboard"
          element={
            <AdminRoute>
              <AdminDashboardPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <UsersManagementPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/documents"
          element={<Navigate to="/admin/documents-moderation" replace />}
        />
        <Route
          path="/admin/exequatur"
          element={
            <AdminRoute>
              <ExequaturVerificationPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/materials"
          element={
            <AdminRoute>
              <MaterialsManagementPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/metrics"
          element={
            <AdminRoute>
              <MetricsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/activity"
          element={
            <AdminRoute>
              <ActivityPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users/:id"
          element={
            <AdminRoute>
              <UserDetailPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <AdminRoute>
              <AdminReportsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <AdminRoute>
              <AdminSettingsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/cases"
          element={
            <AdminRoute>
              <AdminCasesPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/documents-moderation"
          element={
            <AdminRoute>
              <DocumentsModerationPage />
            </AdminRoute>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
    </Routes>
  );
};

// Create Sentry-enhanced BrowserRouter
// const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const App: React.FC = () => {
  // Cargar idioma guardado
  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage && (savedLanguage === "es" || savedLanguage === "en")) {
      import("./i18n/config").then(({ default: i18n }) => {
        i18n.changeLanguage(savedLanguage);
      });
    }
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ReportErrorProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </ReportErrorProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
